import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  UserPlus, CreditCard, Calendar, Vote, FileText, 
  ShoppingCart, Car, MessageCircle, Bell, Clock,
  Loader2, Activity, PartyPopper, CheckCircle2
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '../../lib/utils';

const actionIcons = {
  member_joined: { icon: UserPlus, color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/30' },
  payment_made: { icon: CreditCard, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/30' },
  itinerary_added: { icon: Calendar, color: 'text-purple-500', bg: 'bg-purple-100 dark:bg-purple-900/30' },
  vote_cast: { icon: Vote, color: 'text-orange-500', bg: 'bg-orange-100 dark:bg-orange-900/30' },
  trip_confirmed: { icon: PartyPopper, color: 'text-green-600', bg: 'bg-green-100 dark:bg-green-900/30' },
  file_uploaded: { icon: FileText, color: 'text-slate-500', bg: 'bg-slate-100 dark:bg-slate-800' },
  shopping_list_created: { icon: ShoppingCart, color: 'text-pink-500', bg: 'bg-pink-100 dark:bg-pink-900/30' },
  carpool_created: { icon: Car, color: 'text-cyan-500', bg: 'bg-cyan-100 dark:bg-cyan-900/30' },
  faq_answered: { icon: MessageCircle, color: 'text-yellow-500', bg: 'bg-yellow-100 dark:bg-yellow-900/30' },
  reminder_sent: { icon: Bell, color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/30' },
  default: { icon: Activity, color: 'text-slate-500', bg: 'bg-slate-100 dark:bg-slate-800' }
};

export function ActivityTimeline({ tripId }) {
  const { api } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivities();
  }, [tripId]);

  const fetchActivities = async () => {
    try {
      const response = await api.get(`/trips/${tripId}/activity-log?limit=50`);
      setActivities(response.data);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionConfig = (actionType) => {
    return actionIcons[actionType] || actionIcons.default;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200 dark:border-slate-700">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-orange-500" />
            Atividades Recentes
          </CardTitle>
          <Badge variant="outline" className="text-xs">
            {activities.length} evento{activities.length !== 1 ? 's' : ''}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent>
        {activities.length === 0 ? (
          <div className="text-center py-8 text-slate-500 dark:text-slate-400">
            <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Nenhuma atividade registrada ainda</p>
            <p className="text-sm mt-1">As ações da viagem aparecerão aqui</p>
          </div>
        ) : (
          <ScrollArea className="h-[400px] pr-4">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-200 dark:bg-slate-700" />
              
              <AnimatePresence>
                {activities.map((activity, index) => {
                  const config = getActionConfig(activity.action_type);
                  const IconComponent = config.icon;
                  const activityDate = typeof activity.created_at === 'string' 
                    ? parseISO(activity.created_at) 
                    : activity.created_at;
                  
                  return (
                    <motion.div
                      key={activity.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="relative pl-12 pb-6 last:pb-0"
                    >
                      {/* Icon */}
                      <div className={cn(
                        "absolute left-2 w-7 h-7 rounded-full flex items-center justify-center",
                        config.bg
                      )}>
                        <IconComponent className={cn("w-4 h-4", config.color)} />
                      </div>
                      
                      {/* Content */}
                      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm text-slate-900 dark:text-white">
                              {activity.description}
                            </p>
                            {activity.user_name && (
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                por {activity.user_name}
                              </p>
                            )}
                          </div>
                          <span className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">
                            {formatDistanceToNow(activityDate, { 
                              addSuffix: true, 
                              locale: ptBR 
                            })}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
